// Custom stepper inspired by ReactBits, themed for ByteSizedAI
import React, { useState, Children, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

export function Step({ children }) {
  return <div style={{ padding: '0 1.25rem' }}>{children}</div>;
}

export default function Stepper({
  children,
  initialStep = 1,
  onStepChange,
  accentColor = '#0891b2',
  dark = false,
}) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [direction, setDirection] = useState(0);
  const stepsArray = Children.toArray(children);
  const totalSteps = stepsArray.length;

  const goTo = (step) => {
    if (step < 1 || step > totalSteps || step === currentStep) return;
    setDirection(step > currentStep ? 1 : -1);
    setCurrentStep(step);
    onStepChange?.(step);
  };

  return (
    <div>
      {/* Step indicators */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '0.75rem 0', gap: 0 }}>
        {stepsArray.map((_, index) => {
          const stepNum = index + 1;
          const isActive = stepNum === currentStep;
          const isComplete = stepNum < currentStep;
          const isLast = index === totalSteps - 1;

          return (
            <React.Fragment key={stepNum}>
              <motion.button
                onClick={() => goTo(stepNum)}
                style={{
                  width: 28, height: 28,
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none',
                  cursor: 'pointer',
                  flexShrink: 0,
                  fontSize: 11,
                  fontWeight: 700,
                }}
                animate={{
                  backgroundColor: isActive || isComplete ? accentColor : dark ? '#1f2937' : '#e5e7eb',
                  color: isActive || isComplete ? '#fff' : dark ? '#6b7280' : '#9ca3af',
                  scale: isActive ? 1.15 : 1,
                }}
                whileHover={{ scale: 1.1 }}
                transition={{ duration: 0.25 }}
              >
                {isComplete ? (
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <motion.path
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.3 }}
                      strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  stepNum
                )}
              </motion.button>
              {!isLast && (
                <div style={{
                  flex: 1, height: 2, borderRadius: 1, position: 'relative',
                  margin: '0 4px',
                  backgroundColor: dark ? '#374151' : '#e5e7eb',
                  overflow: 'hidden',
                }}>
                  <motion.div
                    style={{ position: 'absolute', top: 0, left: 0, height: '100%', backgroundColor: accentColor }}
                    animate={{ width: isComplete ? '100%' : '0%' }}
                    transition={{ duration: 0.35 }}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step content with slide animation */}
      <StepContent currentStep={currentStep} direction={direction}>
        {stepsArray[currentStep - 1]}
      </StepContent>
    </div>
  );
}

function StepContent({ currentStep, direction, children }) {
  const [height, setHeight] = useState('auto');

  return (
    <motion.div
      style={{ position: 'relative', overflow: 'hidden' }}
      animate={{ height }}
      transition={{ type: 'spring', duration: 0.4, bounce: 0.1 }}
    >
      <AnimatePresence initial={false} mode="sync" custom={direction}>
        <SlideTransition key={currentStep} direction={direction} onHeight={setHeight}>
          {children}
        </SlideTransition>
      </AnimatePresence>
    </motion.div>
  );
}

const slideVariants = {
  enter: (dir) => ({ x: dir >= 0 ? '100%' : '-100%', opacity: 0 }),
  center: { x: '0%', opacity: 1 },
  exit: (dir) => ({ x: dir >= 0 ? '-50%' : '50%', opacity: 0 }),
};

function SlideTransition({ children, direction, onHeight }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (ref.current) onHeight(ref.current.offsetHeight);
  }, [children, onHeight]);

  return (
    <motion.div
      ref={ref}
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.35 }}
      style={{ position: 'absolute', left: 0, right: 0, top: 0 }}
    >
      {children}
    </motion.div>
  );
}
